import React, { useEffect, useRef, useState } from 'react';
import { PaginationProps } from './../../../types/search';
import useIsMobile from './../../hooks/is-mobile';

const getPaginationItems = (
  currentPage: number,
  totalPages: number,
  isSmallScreen: boolean = false,
  direction: 'forward' | 'backward' | null = null
): (number | string)[] => {
  if (isSmallScreen) {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, '...', totalPages];
    } else if (currentPage >= totalPages - 1) {
      return [1, '...', totalPages - 1, totalPages];
    } else if (direction === 'forward' || direction === null) {
      return [currentPage, currentPage + 1, '...', totalPages];
    } else {
      return [1, '...', currentPage - 1, currentPage];
    }
  }

  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, '...', totalPages];
  } else if (currentPage >= totalPages - 2) {
    return [1, '...', totalPages - 2, totalPages - 1, totalPages];
  } else {
    return [1, '...', currentPage, '...', totalPages];
  }
};

const SearchPagination = ({ currentPage, totalPages, onPageChange, prevPageLabel, nextPageLabel }: PaginationProps) => {
  const isMobile = useIsMobile(1023);
  const isSmallScreen = useIsMobile(767);
  const prevPageRef = useRef(currentPage);
  const [navDirection, setNavDirection] = useState<'forward' | 'backward' | null>(null);

  useEffect(() => {
    if (prevPageRef.current !== currentPage) {
      // Determine navigation direction
      if (currentPage > prevPageRef.current) {
        setNavDirection('forward');
      } else if (currentPage < prevPageRef.current) {
        setNavDirection('backward');
      }

      const scrollToContainerTop = () => {
        const facetsContainer = document.querySelector('.listing__container');

        if (facetsContainer) {
          const navHeight = isMobile ? 65 : 78;

          const containerTop = facetsContainer.getBoundingClientRect().top + window.scrollY;
          const scrollPosition = containerTop - navHeight;

          window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth',
          });
        }
      };

      requestAnimationFrame(scrollToContainerTop);

      prevPageRef.current = currentPage;
    }
  }, [currentPage, isMobile]);

  if (totalPages <= 1) {
    return null;
  }

  const handlePageClick = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const paginationItems = getPaginationItems(currentPage, totalPages, isSmallScreen, navDirection);

  return (
    <nav className="search__pagination" aria-label="Search results pages">
      <button
        onClick={() => handlePageClick(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label={prevPageLabel}
        className="search__pagination-button search__pagination-button--prev"
      >
        <div className="search__pagination-button-content">
          <span>{prevPageLabel}</span>
        </div>
      </button>
      <ul className="search__pagination-list">
        {paginationItems.map((item, index) => (
          <li
            key={typeof item === 'number' ? `page-${item}` : `pagenumber-${index}`}
            className={
              typeof item === 'number' && item === currentPage
                ? 'search__pagination-item search__pagination-item--active'
                : 'search__pagination-item'
            }
          >
            {typeof item === 'number' ? (
              <button
                className={
                  typeof item === 'number' && item === currentPage
                    ? 'search__pagination-number-button search__pagination-number-button--active'
                    : 'search__pagination-number-button'
                }
                onClick={() => handlePageClick(item)}
                aria-current={item === currentPage ? 'page' : undefined}
              >
                {item}
              </button>
            ) : (
              <span className="search__pagination-ellipsis">{item}</span>
            )}
          </li>
        ))}
      </ul>
      <button
        onClick={() => handlePageClick(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label={nextPageLabel}
        className="search__pagination-button search__pagination-button--next"
      >
        <div className="search__pagination-button-content">
          <span>{nextPageLabel}</span>
        </div>
      </button>
    </nav>
  );
};

export default SearchPagination;
